import React from 'react';

export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When set, shows error border + error focus-ring + helper text */
  error?: string;
}

// Base control classes — applied always.
// Design system glass: mesmo campo do Input (surface-field, borda de campo, raio 12),
// multilinha. Mantém a altura mínima maior do textarea.
const BASE =
  'w-full min-h-[124px] resize-none bg-snapfy-field border border-snapfy-border-field rounded-input backdrop-blur-[16px] ' +
  'text-[13px] leading-[1.45] text-snapfy-text-primary px-3 py-2.5 outline-none ' +
  'placeholder:text-snapfy-text-placeholder ' +
  'transition-[box-shadow,border-color,background] duration-300';

// Focus (in-use) state — keeps inner background, adds purple border + focus ring
const FOCUS = 'focus:border-snapfy-accent-border focus:ring-snapfy-focus';

const DISABLED = 'disabled:opacity-50 disabled:cursor-not-allowed';

// Error state — documented exception per DESIGN_SYSTEM.md §Declared ambiguities #5:
// no --snapfy-error-ring token; rgba() form used (not hex) to satisfy the zero-hex grep gate.
const ERROR_BORDER = 'border-snapfy-error';
const ERROR_FOCUS  = 'focus:border-snapfy-error focus:[box-shadow:0_0_0_2px_rgba(239,68,68,0.4)]';

export const Textarea: React.FC<TextareaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  id,
  className = '',
  ...rest
}) => {
  const textareaId = id ?? (label ? `textarea-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  const controlClasses = [
    BASE,
    error ? `${ERROR_BORDER} ${ERROR_FOCUS}` : FOCUS,
    DISABLED,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={textareaId}
          className="block text-[13px] font-semibold text-snapfy-text-primary mb-1.5"
        >
          {label}
        </label>
      )}

      <textarea
        {...rest}
        id={textareaId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && textareaId ? `${textareaId}-error` : undefined}
        className={controlClasses}
      />

      {error && (
        <p
          id={textareaId ? `${textareaId}-error` : undefined}
          role="alert"
          className="text-xs text-snapfy-error mt-1.5"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default Textarea;
